import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ExternalLink, TrendingUp } from 'lucide-react';
import { portfolioProjects } from '../data/mockData';

const Portfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Education Platform', 'B2B Manufacturing', 'Personal Brand', 'E-commerce', 'SaaS Platform'];

  const filteredProjects =
    selectedCategory === 'All'
      ? portfolioProjects
      : portfolioProjects.filter((project) => project.category === selectedCategory);

  return (
    <div className="min-h-screen bg-slate-950 pt-24 pb-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-amber-500/10 text-amber-400 border-amber-500/20">
            Our Work
          </Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Portfolio of Success Stories
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Explore our recent projects and see how we've helped businesses achieve
            remarkable growth through exceptional web design.
          </p>
        </div>

        {/* Category Filter */}
        <div className="mb-12">
          <Tabs defaultValue="All" className="w-full">
            <TabsList className="bg-slate-900 border border-slate-800 p-1 flex flex-wrap justify-center gap-2 h-auto">
              {categories.map((category) => (
                <TabsTrigger
                  key={category}
                  value={category}
                  onClick={() => setSelectedCategory(category)}
                  className="data-[state=active]:bg-amber-500 data-[state=active]:text-white text-gray-400 hover:text-white transition-colors"
                >
                  {category}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value={selectedCategory} className="mt-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {filteredProjects.map((project) => (
                  <Card
                    key={project.id}
                    className="bg-slate-800/50 border-slate-700 hover:border-amber-500/50 transition-all duration-300 overflow-hidden group"
                  >
                    <div className="relative h-64 overflow-hidden">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent opacity-80" />
                      
                      <div className="absolute top-4 right-4">
                        <Button
                          size="sm"
                          className="bg-white/10 backdrop-blur-md hover:bg-white/20 text-white border-0"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>

                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-3">
                        <Badge className="bg-amber-500/10 text-amber-400 border-0">
                          {project.category}
                        </Badge>
                      </div>

                      <h3 className="text-2xl font-bold text-white mb-2">
                        {project.title}
                      </h3>

                      <p className="text-gray-400 mb-4">{project.description}</p>

                      <div className="flex flex-wrap gap-2 mb-4">
                        {project.tags.map((tag, index) => (
                          <Badge
                            key={index}
                            variant="outline"
                            className="border-slate-600 text-gray-400"
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>

                      <div className="flex items-center text-green-400 font-semibold bg-green-400/10 rounded-lg px-4 py-2">
                        <TrendingUp className="w-4 h-4 mr-2" />
                        {project.results}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Results Section */}
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 md:p-12 border border-slate-800 mt-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-4">
              Real Results for Real Businesses
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Our data-driven approach consistently delivers measurable improvements
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-400 mb-2">2.5x</div>
              <div className="text-sm text-gray-400">Avg. Conversion Increase</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-400 mb-2">180%</div>
              <div className="text-sm text-gray-400">Avg. Traffic Growth</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-400 mb-2">98%</div>
              <div className="text-sm text-gray-400">Client Satisfaction</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-amber-400 mb-2">150+</div>
              <div className="text-sm text-gray-400">Projects Delivered</div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Join Our Success Stories?
          </h2>
          <p className="text-gray-400 mb-8">
            Let's create a website that drives real results for your business
          </p>
          <Button
            size="lg"
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold px-8 shadow-lg shadow-amber-500/20"
            onClick={() => (window.location.href = '/contact')}
          >
            Start Your Project
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Portfolio;
